import useHttpQuery from '@actions/services/firestore/browser/useHttpQuery';
import { SystemAgentDetailResponse, UseSystemAgentInput, UseSystemAgentResult } from './types';

/**
 * Hook for fetching a single system agent's full details from the API
 *
 * Fetches tools, toolboxes, defaults, and metadata for a specific agent type.
 * Uses useHttpQuery for efficient caching and error handling.
 *
 * @param options - Configuration for the hook
 * @param options.agentType - The agent type identifier to fetch
 * @param options.enabled - Whether the query should be enabled
 * @returns Object containing systemAgent detail, loading state, error state, and refetch function
 *
 * @example
 * ```tsx
 * const { systemAgent, isLoadingSystemAgent } = useSystemAgent({
 *     agentType: 'promptEngineer',
 *     enabled: true,
 * });
 * ```
 */
const useSystemAgent = ({ agentType, enabled }: UseSystemAgentInput): UseSystemAgentResult => {
    const { data, isLoading, error, refetch } = useHttpQuery<SystemAgentDetailResponse>({
        pathname: `/system-agents/${agentType}`,
        method: 'GET',
        key: ['system-agent', agentType],
        queryOptions: { enabled: enabled && !!agentType },
    });

    return {
        systemAgent: data?.data || null,
        isLoadingSystemAgent: isLoading,
        error: error || undefined,
        refetch,
    };
};

export default useSystemAgent;
